/**
 * Settings domain types + zod schemas.
 *
 * Kept colocated with the `features/settings` feature folder so that
 * both the React Query hook and the form validators share the same
 * source of truth.
 *
 * When the backend `GET /api/v1/business/settings` endpoint is wired,
 * these types become the response/request contract. Until then the
 * hook in `settings-api.ts` returns mocked data matching this shape.
 */
import { z } from 'zod';

export const profileSchema = z.object({
  legalName: z.string().min(2).max(120),
  displayName: z.string().min(2).max(80),
  description: z.string().max(500).optional().or(z.literal('')),
  email: z.string().email(),
  phone: z
    .string()
    .min(6)
    .max(20)
    .regex(/^[0-9 +()-]+$/)
    .or(z.literal('')),
  website: z.string().url().optional().or(z.literal('')),
  addressLine: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(80).optional().or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  timezone: z.string().min(1),
  currency: z.enum(['TRY', 'EUR', 'USD', 'GBP']),
  locale: z.enum(['tr', 'en']),
});

export type BusinessProfileSettings = z.infer<typeof profileSchema>;

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const dayHoursSchema = z
  .object({
    closed: z.boolean(),
    open: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional()
      .or(z.literal('')),
    close: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional()
      .or(z.literal('')),
  })
  .superRefine((val, ctx) => {
    if (val.closed) return;
    if (!val.open || !val.close) return;
    if (val.close <= val.open) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['close'],
        message: 'invalidRange',
      });
    }
  });

export type DayHours = z.infer<typeof dayHoursSchema>;

export const hoursSchema = z.object({
  monday: dayHoursSchema,
  tuesday: dayHoursSchema,
  wednesday: dayHoursSchema,
  thursday: dayHoursSchema,
  friday: dayHoursSchema,
  saturday: dayHoursSchema,
  sunday: dayHoursSchema,
});

export type WeeklyHours = z.infer<typeof hoursSchema>;

export const NOTIFICATION_EVENTS = [
  'newBooking',
  'cancellation',
  'payment',
  'lowStock',
  'dailyDigest',
] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const NOTIFICATION_CHANNELS = ['email', 'sms', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const notificationPrefsSchema = z.object({
  newBooking: z.object({ email: z.boolean(), sms: z.boolean(), push: z.boolean() }),
  cancellation: z.object({ email: z.boolean(), sms: z.boolean(), push: z.boolean() }),
  payment: z.object({ email: z.boolean(), sms: z.boolean(), push: z.boolean() }),
  lowStock: z.object({ email: z.boolean(), sms: z.boolean(), push: z.boolean() }),
  dailyDigest: z.object({ email: z.boolean(), sms: z.boolean(), push: z.boolean() }),
});

export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export interface SessionInfo {
  id: string;
  deviceLabel: string;
  ipAddress: string;
  lastActiveAt: string;
  isCurrent: boolean;
}

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessions: SessionInfo[];
}

export interface BusinessSettingsPayload {
  profile: BusinessProfileSettings;
  hours: WeeklyHours;
  notifications: NotificationPrefs;
  security: SecuritySettings;
}
